export type JsonValue = string | number | boolean | null | readonly JsonValue[] | { readonly [key: string]: JsonValue }

export interface NativeAuthTokens {
  readonly access: string
  readonly refresh: string
  readonly user?: JsonValue
  readonly [key: string]: JsonValue | undefined
}

const ACCESS_TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000

const isRecord = (value: unknown): value is Record<string, JsonValue> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))

export const parseNativeAuthTokens = (value: unknown): NativeAuthTokens | null => {
  if (
    !isRecord(value) ||
    typeof value.access !== 'string' ||
    !value.access ||
    typeof value.refresh !== 'string' ||
    !value.refresh
  ) {
    return null
  }
  return Object.freeze({ ...value }) as NativeAuthTokens
}

const parseJwtPayload = (token: string): Record<string, JsonValue> | null => {
  const parts = token.split('.')
  if (parts.length !== 3 || parts[1].length % 4 === 1) return null
  const base64 = parts[1]
    .replaceAll('-', '+')
    .replaceAll('_', '/')
    .padEnd(Math.ceil(parts[1].length / 4) * 4, '=')

  try {
    const payload: unknown = JSON.parse(globalThis.atob(base64))
    return isRecord(payload) ? payload : null
  } catch {
    return null
  }
}

export const isNativeAccessTokenCurrent = (value: unknown, now = Date.now()): boolean => {
  const tokens = parseNativeAuthTokens(value)
  if (!tokens) return false
  const payload = parseJwtPayload(tokens.access)
  return (
    typeof payload?.exp === 'number' &&
    Number.isFinite(payload.exp) &&
    payload.exp * 1000 > now + ACCESS_TOKEN_EXPIRY_BUFFER_MS
  )
}
